"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

/**
 * Registers global capture-phase scroll listeners once to normalize event.target
 * for Radix tooltip dismiss logic (Text nodes lack Element.contains).
 * Mount once near the app root — not per tooltip instance.
 */
function TooltipScrollGuard() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  React.useEffect(_temp2, t0);
  return null;
}
function _temp2() {
  const handleScroll = _temp;
  const options = {
    capture: true,
    passive: true
  };
  window.addEventListener("scroll", handleScroll, options);
  document.addEventListener("scroll", handleScroll, options);
  return () => {
    window.removeEventListener("scroll", handleScroll, options);
    document.removeEventListener("scroll", handleScroll, options);
  };
}
function _temp(event) {
  if (event.target && !(event.target instanceof Element)) {
    const node = event.target as Node;
    let element;
    if (node.nodeType === Node.TEXT_NODE) {
      element = node.parentElement;
    } else {
      if (node.nodeType === Node.DOCUMENT_NODE) {
        element = (node as Document).documentElement;
      } else {
        element = node.parentElement;
      }
    }
    if (element) {
      try {
        Object.defineProperty(event, "target", {
          value: element,
          writable: false,
          configurable: true
        });
      } catch {}
    }
  }
}
function TooltipProvider(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      delayDuration: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const delayDuration = t1 === undefined ? 0 : t1;
  let t2;
  if ($[3] !== delayDuration || $[4] !== props) {
    t2 = <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
    $[3] = delayDuration;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Tooltip(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function TooltipTrigger(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function TooltipContent(t0) {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const sideOffset = t1 === undefined ? 0 : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("bg-primary-800/90 border-gray-400 border text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[10000] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props.style) {
    t3 = {
      zIndex: 10000,
      ...props.style
    };
    $[7] = props.style;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== sideOffset || $[12] !== t2 || $[13] !== t3) {
    t4 = <TooltipPrimitive.Portal container={typeof document !== "undefined" ? document.body : undefined}><TooltipPrimitive.Content data-slot="tooltip-content" sideOffset={sideOffset} className={t2} style={t3} {...props}>{children}</TooltipPrimitive.Content></TooltipPrimitive.Portal>;
    $[9] = children;
    $[10] = props;
    $[11] = sideOffset;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipScrollGuard };